/**
 * Seam titles and summaries from GET /explorer/api/seams, fetched once per
 * scope per session. Only asked for when a result actually crossed a seam.
 *
 * A failed request (the route is E5's and may not be deployed yet) resolves to
 * null and is remembered too, so the notice falls back to seam ids without
 * retrying on every result.
 */
import { useEffect, useState } from "react";
import type { ExplorerAPI } from "../util/Requests";
import type { SeamInfo } from "../models/Explorer";

export type SeamCatalog = Record<string, SeamInfo>;

const catalogs = new Map<string, Promise<SeamCatalog | null>>();

export function loadSeamCatalog(api: ExplorerAPI): Promise<SeamCatalog | null> {
  const cached = catalogs.get(api.scope);
  if (cached) return cached;
  const request = api
    .getSeams()
    .then(({ data }) => {
      const seams = Array.isArray(data?.seams) ? data.seams : [];
      return Object.fromEntries(seams.map((seam) => [seam.seam, seam]));
    })
    .catch(() => null);
  catalogs.set(api.scope, request);
  return request;
}

/** Test hook: forget cached catalogs. */
export function resetSeamCatalogs(): void {
  catalogs.clear();
}

/**
 * `undefined` while loading (or when not needed), `null` when the route is
 * unavailable, the catalog otherwise.
 */
export function useSeamCatalog(
  api: ExplorerAPI,
  enabled: boolean,
): SeamCatalog | null | undefined {
  const [catalog, setCatalog] = useState<SeamCatalog | null | undefined>(
    undefined,
  );
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    loadSeamCatalog(api).then((result) => {
      if (!cancelled) setCatalog(result);
    });
    return () => {
      cancelled = true;
    };
  }, [api, enabled]);
  return enabled ? catalog : undefined;
}
